/**
 * /settings/loose-services (v9) — cadastro de Serviços avulsos no padrão
 * TiFlux: breadcrumb Configurações / Financeiro / Serviços avulsos, busca
 * client-side, "+ Serviço avulso" teal à direita e tabela Nome | Clientes |
 * Valor hora interno | Valor hora remoto | Valor hora externo | ações.
 * Dialog: Nome* + Cliente (default Todos) + os 3 valores-hora*. Leitura livre
 * p/ staff; escrita exige contracts.manage (botões escondidos).
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { usePermissions } from '@/auth/use-permissions';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Dialog, EmptyState, Skeleton } from '@/components/ui/misc';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import { clientsLookupOptions } from '@/features/desks/desks-queries';
import {
  matchesText,
  normalizeText,
  SettingsBreadcrumb,
  SettingsSearch,
} from '@/features/settings/settings-ui';
import type { LooseService } from '@erp/shared';
import { errorMessage, looseServicesOptions } from './billing-queries';
import { useDeleteLooseService, useSaveLooseService } from './billing-mutations';
import { formatBRL, formatBRLInput, parseBRLInput } from './billing-format';
import { MoneyInput } from './contract-drawer';

export function LooseServicesSettingsPage() {
  const { can } = usePermissions();
  const canManage = can('contracts.manage');
  const listQ = useQuery(looseServicesOptions());
  const remove = useDeleteLooseService();
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState<{ service: LooseService | null } | null>(null);

  const q = normalizeText(query.trim());
  const list = (listQ.data ?? []).filter(
    (s) => q === '' || matchesText(s.name, q) || matchesText(s.clientName ?? 'Todos', q)
  );

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb section="Financeiro" page="Serviços avulsos" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Serviços avulsos</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar serviço avulso" />
          {canManage ? (
            <Button size="sm" className="h-9" onClick={() => setDialog({ service: null })}>
              <Plus className="size-4" strokeWidth={2.5} />
              Serviço avulso
            </Button>
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        {listQ.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : listQ.isError ? (
          <EmptyState message={`Erro ao carregar serviços avulsos: ${errorMessage(listQ.error)}`} />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={q !== '' ? 'Nenhum serviço avulso encontrado' : 'Não há dados'}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 px-4 font-semibold">Clientes</th>
                  <th className="h-10 w-40 px-4 font-semibold">Valor hora interno</th>
                  <th className="h-10 w-40 px-4 font-semibold">Valor hora remoto</th>
                  <th className="h-10 w-40 px-4 font-semibold">Valor hora externo</th>
                  <th className="h-10 w-24 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr
                    key={s.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-72 truncate px-4 font-medium">{s.name}</td>
                    <td className="max-w-56 truncate px-4">{s.clientName ?? 'Todos'}</td>
                    <td className="px-4 tabular-nums whitespace-nowrap">
                      {formatBRL(s.internalHourValue)}
                    </td>
                    <td className="px-4 tabular-nums whitespace-nowrap">
                      {formatBRL(s.remoteHourValue)}
                    </td>
                    <td className="px-4 tabular-nums whitespace-nowrap">
                      {formatBRL(s.externalHourValue)}
                    </td>
                    <td className="px-4">
                      {canManage ? (
                        <div className="flex items-center justify-end gap-0.5">
                          <DeskRowAction
                            label="Editar serviço avulso"
                            onClick={() => setDialog({ service: s })}
                          >
                            <Pencil className="size-3.5" />
                          </DeskRowAction>
                          <DeskRowAction
                            label="Excluir serviço avulso"
                            destructive
                            disabled={remove.isPending}
                            onClick={() => {
                              if (window.confirm(`Excluir o serviço avulso "${s.name}"?`)) {
                                remove.mutate(s.id);
                              }
                            }}
                          >
                            <Trash2 className="size-3.5" />
                          </DeskRowAction>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {remove.error ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage(remove.error)}</p>
      ) : null}

      {dialog ? (
        <LooseServiceDialog service={dialog.service} onClose={() => setDialog(null)} />
      ) : null}
    </div>
  );
}

function LooseServiceDialog({
  service,
  onClose,
}: {
  service: LooseService | null;
  onClose: () => void;
}) {
  const save = useSaveLooseService();
  const clientsQ = useQuery(clientsLookupOptions());

  const [name, setName] = useState(service?.name ?? '');
  const [clientId, setClientId] = useState<number | ''>(service?.clientId ?? '');
  const [internalText, setInternalText] = useState(
    service ? formatBRLInput(service.internalHourValue) : ''
  );
  const [remoteText, setRemoteText] = useState(
    service ? formatBRLInput(service.remoteHourValue) : ''
  );
  const [externalText, setExternalText] = useState(
    service ? formatBRLInput(service.externalHourValue) : ''
  );

  const internal = parseBRLInput(internalText);
  const remote = parseBRLInput(remoteText);
  const external = parseBRLInput(externalText);
  const valid = name.trim() !== '' && internal !== null && remote !== null && external !== null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || internal === null || remote === null || external === null) return;
    save.mutate(
      {
        id: service?.id ?? null,
        body: {
          name: name.trim(),
          clientId: clientId === '' ? null : clientId,
          internalHourValue: internal,
          remoteHourValue: remote,
          externalHourValue: external,
        },
      },
      { onSuccess: onClose }
    );
  };

  return (
    <Dialog open onClose={onClose} title={service ? 'Editar serviço avulso' : 'Novo serviço avulso'}>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="loose-service-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="loose-service-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="loose-service-client">Cliente</Label>
          <Select
            id="loose-service-client"
            value={clientId === '' ? '' : String(clientId)}
            onChange={(e) => setClientId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Todos</option>
            {(clientsQ.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="loose-service-internal">
            Valor hora interno <span className="text-destructive">*</span>
          </Label>
          <MoneyInput
            id="loose-service-internal"
            value={internalText}
            onChange={setInternalText}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="loose-service-remote">
            Valor hora remoto <span className="text-destructive">*</span>
          </Label>
          <MoneyInput
            id="loose-service-remote"
            value={remoteText}
            onChange={setRemoteText}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="loose-service-external">
            Valor hora externo <span className="text-destructive">*</span>
          </Label>
          <MoneyInput
            id="loose-service-external"
            value={externalText}
            onChange={setExternalText}
            required
          />
        </div>
        {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !valid}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
